import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { JSONTransformer } from '@atlaskit/editor-json-transformer/JSONTransformer-2';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { expVal } from '@atlaskit/tmp-editor-statsig/expVal';
import { getXProductHeader } from '@atlassian/editor-ai-common/api/request';

import type { AIPlugin } from '../../aiPluginType';
import type { EditorPluginAIProvider } from '../../types/types';
import {
	shouldImproveWritingApiUrl,
	shouldImproveWritingCcSmartsApiUrl,
} from '../../ui/prebuilt/utils';

import {
	fireAiQuickPromptApiErrorEvent,
	fireAiQuickPromptShadowComparisonDivergedEvent,
} from './analytics';
import type { EligibleRange } from './types';

const CONTROL_ROUTING_MODE = 'control';
const SHADOW_COMPARE_ROUTING_MODE = 'shadow_compare';
const LIVE_CC_SMARTS_ROUTING_MODE = 'live_cc_smarts';

type ShouldImproveWritingRoutingMode =
	| typeof CONTROL_ROUTING_MODE
	| typeof SHADOW_COMPARE_ROUTING_MODE
	| typeof LIVE_CC_SMARTS_ROUTING_MODE;

type FetchErrorType = 'non_ok_response' | 'request_failed' | 'aborted';

type ShouldImproveWritingFetchResult = {
	errorMessage?: string;
	errorType?: FetchErrorType;
	ok: boolean;
	shouldImprove: boolean;
	status?: number;
};

type ShouldImproveWritingRequestContext = {
	adfContent: string;
	locale: string;
	product: EditorPluginAIProvider['product'];
	signal?: AbortSignal;
};

type ObservabilityContext = {
	api?: ExtractInjectionAPI<AIPlugin>;
	experienceName: string;
};

const getShouldImproveWritingRoutingMode = (): ShouldImproveWritingRoutingMode => {
	try {
		return expVal(
			'cc_smarts_should_improve_writing_migration',
			'routingMode',
			CONTROL_ROUTING_MODE,
		) as ShouldImproveWritingRoutingMode;
	} catch {
		// Fall back to convo-ai without reporting an API error.
		return CONTROL_ROUTING_MODE;
	}
};

const fetchShouldImproveWritingFromBackend = async (
	url: string,
	{ adfContent, locale, product, signal }: ShouldImproveWritingRequestContext,
): Promise<ShouldImproveWritingFetchResult> => {
	try {
		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json;charset=UTF-8',
				'X-Experience-Id': 'editor',
				...getXProductHeader(product),
			},
			mode: 'cors',
			credentials: 'include',
			signal,
			body: JSON.stringify({
				ai_feature_input: {
					locale,
					adf_content: adfContent,
				},
			}),
		});

		if (!response.ok) {
			return {
				errorType: 'non_ok_response',
				ok: false,
				shouldImprove: false,
				status: response.status,
			};
		}

		const data = await response.json();
		return {
			ok: true,
			shouldImprove: data?.ai_feature_output?.should_improve === true,
			status: response.status,
		};
	} catch (error) {
		if (error instanceof Error && error.name === 'AbortError') {
			return {
				errorType: 'aborted',
				ok: false,
				shouldImprove: false,
			};
		}

		return {
			errorMessage: error instanceof Error ? error.message : undefined,
			errorType: 'request_failed',
			ok: false,
			shouldImprove: false,
		};
	}
};

const fireVisibleApiError = (
	result: ShouldImproveWritingFetchResult,
	{ api, experienceName }: ObservabilityContext,
) => {
	if (result.ok || result.errorType === 'aborted') {
		return;
	}

	fireAiQuickPromptApiErrorEvent({
		api,
		experienceName,
		errorMessage: result.errorMessage,
		statusCode: result.status,
	});
};

const logShadowComparison = (
	convoAiResult: ShouldImproveWritingFetchResult,
	ccSmartsResult: ShouldImproveWritingFetchResult,
	observabilityContext: ObservabilityContext,
) => {
	const responseHealthMatches =
		convoAiResult.ok === ccSmartsResult.ok && convoAiResult.status === ccSmartsResult.status;
	const shouldImproveMatches = convoAiResult.shouldImprove === ccSmartsResult.shouldImprove;

	if (responseHealthMatches && shouldImproveMatches) {
		return;
	}

	fireAiQuickPromptShadowComparisonDivergedEvent({
		api: observabilityContext.api,
		experienceName: observabilityContext.experienceName,
		convoAiOk: convoAiResult.ok,
		ccSmartsOk: ccSmartsResult.ok,
		convoAiStatus: convoAiResult.status,
		ccSmartsStatus: ccSmartsResult.status,
		convoAiErrorType: convoAiResult.errorType,
		ccSmartsErrorType: ccSmartsResult.errorType,
		convoAiShouldImprove: convoAiResult.shouldImprove,
		ccSmartsShouldImprove: ccSmartsResult.shouldImprove,
	});
};

const checkShouldImproveWritingWithShadowCompare = async (
	requestContext: ShouldImproveWritingRequestContext,
	observabilityContext: ObservabilityContext,
): Promise<boolean> => {
	const convoAiPromise = fetchShouldImproveWritingFromBackend(
		shouldImproveWritingApiUrl,
		requestContext,
	);
	const ccSmartsPromise = fetchShouldImproveWritingFromBackend(
		shouldImproveWritingCcSmartsApiUrl,
		requestContext,
	);

	const convoAiResult = await convoAiPromise;

	void ccSmartsPromise
		.then((ccSmartsResult) => {
			logShadowComparison(convoAiResult, ccSmartsResult, observabilityContext);
		})
		.catch(() => {
			// Shadow analytics must never affect the user-visible convo-ai result.
		});

	fireVisibleApiError(convoAiResult, observabilityContext);
	return convoAiResult.shouldImprove;
};

/**
 * Call the should-improve-writing API to check whether the content in the
 * given range should be improved before showing the AI quick prompt.
 *
 * Fires an `apiError` analytics event when a network or HTTP error occurs.
 *
 * @returns `true` when the API confirms the content should be improved,
 *          `false` otherwise (including network / parse errors).
 */
export async function checkShouldImproveWriting({
	api,
	experienceName,
	locale,
	product,
	range,
	signal,
	view,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	experienceName: string;
	locale: string;
	product: EditorPluginAIProvider['product'];
	range: EligibleRange;
	/** Optional abort signal to cancel the network request early. */
	signal?: AbortSignal;
	view: EditorView;
}): Promise<boolean> {
	try {
		const { doc, schema } = view.state;
		const $from = doc.resolve(range.from);
		const parentNode = $from.parent;
		const docNode = schema.nodes.doc.createAndFill(null, parentNode);
		if (!docNode) {
			return false;
		}

		const transformer = new JSONTransformer();
		const adfContent = JSON.stringify(transformer.encode(docNode));

		const requestContext: ShouldImproveWritingRequestContext = {
			adfContent,
			locale,
			product,
			signal,
		};
		const observabilityContext: ObservabilityContext = { api, experienceName };
		const routingMode = getShouldImproveWritingRoutingMode();

		if (routingMode === SHADOW_COMPARE_ROUTING_MODE) {
			return checkShouldImproveWritingWithShadowCompare(requestContext, observabilityContext);
		}

		const url =
			routingMode === LIVE_CC_SMARTS_ROUTING_MODE
				? shouldImproveWritingCcSmartsApiUrl
				: shouldImproveWritingApiUrl;
		const result = await fetchShouldImproveWritingFromBackend(url, requestContext);

		fireVisibleApiError(result, observabilityContext);
		return result.shouldImprove;
	} catch (error) {
		fireAiQuickPromptApiErrorEvent({
			api,
			experienceName,
			errorMessage: error instanceof Error ? error.message : undefined,
		});
		return false;
	}
}
